import { useEffect, useMemo, useState } from 'react';
import { ChevronRight, ChevronDown, Plus, X, Search, Network, Loader2 } from 'lucide-react';
import { workItemApi, portfolioApi } from '../services/api';
import { useProjectStore } from '../store/useProjectStore';

type TreeNode = {
  _id: string;
  displayId?: string;
  title: string;
  workItemType: string;
  status: string;
  rolledUpStatus?: string;
  projectId: string;
  parentProjectId?: string;
  rootEpicId?: string;
  storyPoints?: number;
  wsjfScore?: number;
  contributingProjectIds?: string[];
  children?: TreeNode[];
};

const TYPE_BADGE: Record<string, string> = {
  epic: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-200',
  feature: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200',
  story: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-200',
  task: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  bug: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200',
  security: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-200',
};

const STATUS_COLOR: Record<string, string> = {
  backlog: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  planning: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200',
  todo: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200',
  'in-progress': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
  review: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-200',
  done: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-200',
};

function projectColor(id: string): string {
  // Deterministic color per projectId
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  return `hsl(${hue}, 45%, 55%)`;
}

export default function PortfolioPage() {
  const projects = useProjectStore((s) => s.projects);
  const fetchProjects = useProjectStore((s) => s.fetchProjects);

  const [epics, setEpics] = useState<TreeNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedEpicId, setSelectedEpicId] = useState<string>('');
  const [tree, setTree] = useState<TreeNode | null>(null);
  const [treeLoading, setTreeLoading] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [rootProjectFilter, setRootProjectFilter] = useState<string>('');
  const [adoptTarget, setAdoptTarget] = useState<TreeNode | null>(null);
  const [parentsTarget, setParentsTarget] = useState<string>('');

  useEffect(() => {
    if (!projects.length) fetchProjects();
  }, [projects.length, fetchProjects]);

  const loadEpics = async () => {
    setLoading(true);
    try {
      const data = await workItemApi.getPortfolioEpics(rootProjectFilter || undefined);
      setEpics(data as TreeNode[]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEpics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootProjectFilter]);

  const loadTree = async (epicId: string) => {
    setTreeLoading(true);
    try {
      const t = await workItemApi.getTree(epicId, 5);
      setTree(t as TreeNode);
      setExpanded(new Set([epicId]));
    } finally {
      setTreeLoading(false);
    }
  };

  const handleSelectEpic = (id: string) => {
    setSelectedEpicId(id);
    loadTree(id);
  };

  const toggleExpand = (id: string) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  };

  const projectName = useMemo(() => {
    const map: Record<string, string> = {};
    projects.forEach((p: any) => { map[p._id] = p.name; });
    return map;
  }, [projects]);

  // Summary stats for the dashboard
  const summary = useMemo(() => {
    if (!tree) return null;
    let nodes = 0;
    let totalSP = 0;
    const statusCounts: Record<string, number> = {};
    const projectCounts: Record<string, number> = {};
    const walk = (n: TreeNode) => {
      nodes++;
      totalSP += n.storyPoints || 0;
      const st = n.status || 'backlog';
      statusCounts[st] = (statusCounts[st] || 0) + 1;
      projectCounts[n.projectId] = (projectCounts[n.projectId] || 0) + 1;
      (n.children || []).forEach(walk);
    };
    walk(tree);
    return { nodes, totalSP, statusCounts, projectCounts };
  }, [tree]);

  return (
    <div className="p-6 max-w-full">
      <div className="flex items-center gap-3 mb-4">
        <Network className="text-accent" size={24} />
        <h1 className="text-2xl font-bold">Portfolio</h1>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          Cross-project Epic nesting & rollup
        </span>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[320px_1fr] gap-4">
        {/* Left: Epic list */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold">Portfolio Epics</h2>
            <button
              onClick={() => setParentsTarget(rootProjectFilter || (projects[0]?._id || ''))}
              className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
              disabled={!projects.length}
              title="Manage which projects may adopt work items from a project"
            >
              Governance
            </button>
          </div>

          <select
            value={rootProjectFilter}
            onChange={(e) => setRootProjectFilter(e.target.value)}
            className="w-full mb-3 px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900"
          >
            <option value="">All projects</option>
            {projects.map((p: any) => (
              <option key={p._id} value={p._id}>{p.name}</option>
            ))}
          </select>

          {loading ? (
            <div className="flex items-center justify-center p-6 text-gray-500">
              <Loader2 className="animate-spin" size={16} />
            </div>
          ) : epics.length === 0 ? (
            <div className="text-sm text-gray-500 p-3">No portfolio epics.</div>
          ) : (
            <ul className="space-y-1 max-h-[calc(100vh-240px)] overflow-y-auto">
              {epics.map((e) => (
                <li key={e._id}>
                  <button
                    onClick={() => handleSelectEpic(e._id)}
                    className={`w-full text-left px-2 py-2 rounded text-sm ${
                      selectedEpicId === e._id
                        ? 'bg-accent/15 border-l-2 border-accent'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ background: projectColor(e.projectId) }}
                        title={projectName[e.projectId] || e.projectId}
                      />
                      <span className="font-mono text-xs text-gray-500">{e.displayId}</span>
                      <span className="truncate flex-1">{e.title}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs">
                      <span className={`px-1.5 py-0.5 rounded ${STATUS_COLOR[e.status] || STATUS_COLOR.backlog}`}>
                        {e.rolledUpStatus || e.status || '—'}
                      </span>
                      <span className="text-gray-500">SP: {e.storyPoints || 0}</span>
                      {(e.wsjfScore || 0) > 0 && (
                        <span className="text-gray-500">WSJF: {Number(e.wsjfScore).toFixed(2)}</span>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Right: Tree + summary */}
        <div className="space-y-4">
          {summary && tree && (
            <div className="grid grid-cols-3 gap-4">
              <StatCard label="Nodes in subtree" value={summary.nodes} />
              <StatCard label="Rolled-up SP" value={summary.totalSP.toFixed(1)} />
              <StatCard label="Contributing projects" value={Object.keys(summary.projectCounts).length} />
            </div>
          )}

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold">Epic Tree</h2>
              {tree && (
                <button
                  onClick={() => setAdoptTarget(tree)}
                  className="flex items-center gap-1 text-sm px-3 py-1.5 rounded bg-accent text-white hover:bg-accent-dark"
                >
                  <Plus size={14} /> Adopt child
                </button>
              )}
            </div>
            {!selectedEpicId && (
              <div className="text-sm text-gray-500 p-6 text-center">
                Select an epic from the left to view its tree.
              </div>
            )}
            {treeLoading && <div className="p-4 text-gray-500"><Loader2 className="animate-spin inline" size={14}/> Loading tree…</div>}
            {tree && !treeLoading && (
              <TreeRow
                node={tree}
                depth={0}
                expanded={expanded}
                onToggle={toggleExpand}
                onAdopt={setAdoptTarget}
                rootProjectId={tree.projectId}
                projectName={projectName}
              />
            )}
          </div>

          {summary && summary.projectCounts && Object.keys(summary.projectCounts).length > 1 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <h3 className="font-semibold mb-3">Contributing Projects</h3>
              <div className="flex flex-wrap gap-2">
                {Object.entries(summary.projectCounts)
                  .sort((a, b) => b[1] - a[1])
                  .map(([pid, count]) => (
                    <div
                      key={pid}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm"
                      style={{ background: `${projectColor(pid)}22`, border: `1px solid ${projectColor(pid)}` }}
                    >
                      <span className="w-2 h-2 rounded-full" style={{ background: projectColor(pid) }} />
                      <span>{projectName[pid] || pid}</span>
                      <span className="text-gray-500">×{count}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {adoptTarget && (
        <AdoptChildModal
          parent={adoptTarget}
          onClose={() => setAdoptTarget(null)}
          onAdopted={() => {
            setAdoptTarget(null);
            if (selectedEpicId) loadTree(selectedEpicId);
          }}
        />
      )}

      {parentsTarget && (
        <PortfolioParentsModal
          projectId={parentsTarget}
          onClose={() => setParentsTarget('')}
        />
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

function TreeRow({
  node,
  depth,
  expanded,
  onToggle,
  onAdopt,
  rootProjectId,
  projectName,
}: {
  node: TreeNode;
  depth: number;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  onAdopt: (node: TreeNode) => void;
  rootProjectId: string;
  projectName: Record<string, string>;
}) {
  const hasChildren = (node.children || []).length > 0;
  const isOpen = expanded.has(node._id);
  const isCrossProject = node.projectId !== rootProjectId;

  return (
    <div>
      <div
        className="flex items-center gap-2 py-1.5 px-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700/50"
        style={{ paddingLeft: depth * 20 + 4 }}
      >
        <button
          onClick={() => hasChildren && onToggle(node._id)}
          className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-700"
          disabled={!hasChildren}
        >
          {hasChildren ? (isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : null}
        </button>
        <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${TYPE_BADGE[node.workItemType] || TYPE_BADGE.task}`}>
          {node.workItemType}
        </span>
        <span className="font-mono text-xs text-gray-500">{node.displayId}</span>
        <span className="flex-1 text-sm truncate" title={node.title}>{node.title}</span>
        {isCrossProject && (
          <span
            className="px-2 py-0.5 text-xs rounded-full border"
            style={{ borderColor: projectColor(node.projectId), color: projectColor(node.projectId) }}
            title={`Cross-project: ${projectName[node.projectId] || node.projectId}`}
          >
            {projectName[node.projectId] || node.projectId.slice(0, 10)}
          </span>
        )}
        <span className={`px-1.5 py-0.5 text-xs rounded ${STATUS_COLOR[node.rolledUpStatus || node.status] || STATUS_COLOR.backlog}`}>
          {node.rolledUpStatus || node.status || '—'}
        </span>
        <span className="text-xs text-gray-500 w-14 text-right">SP {node.storyPoints || 0}</span>
        {node.workItemType === 'epic' && (
          <button
            onClick={() => onAdopt(node)}
            className="text-xs px-2 py-0.5 rounded border border-gray-300 dark:border-gray-600 hover:bg-accent hover:text-white hover:border-accent"
            title="Adopt a work item as a child of this epic"
          >
            <Plus size={12} />
          </button>
        )}
      </div>

      {hasChildren && isOpen && (
        <div>
          {node.children!.map((c) => (
            <TreeRow
              key={c._id}
              node={c}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              onAdopt={onAdopt}
              rootProjectId={rootProjectId}
              projectName={projectName}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AdoptChildModal({
  parent,
  onClose,
  onAdopted,
}: {
  parent: TreeNode;
  onClose: () => void;
  onAdopted: () => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [adopting, setAdopting] = useState<string>('');
  const [error, setError] = useState<string>('');

  const search = async () => {
    setSearching(true);
    setError('');
    try {
      // Simple substring search across all work items (client filters)
      const all = await workItemApi.list();
      const q = query.trim().toLowerCase();
      const filtered = all
        .filter((w: any) => {
          if (w._id === parent._id) return false;
          if (!q) return true;
          return (
            (w.title || '').toLowerCase().includes(q) ||
            (w.displayId || '').toLowerCase().includes(q)
          );
        })
        .slice(0, 50);
      setResults(filtered);
    } catch (e: any) {
      setError(e.message || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const adopt = async (item: any) => {
    setAdopting(item._id);
    setError('');
    try {
      await workItemApi.update(item._id, {
        parentId: parent._id,
        githubUser: 'ui-user',
      });
      onAdopted();
    } catch (e: any) {
      setError(e.message || 'Adoption failed. Check governance (portfolio-parents).');
    } finally {
      setAdopting('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="font-semibold">Adopt a work item</h2>
            <p className="text-xs text-gray-500 mt-0.5">as a child of <strong>{parent.displayId}</strong> — {parent.title}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
            <X size={16} />
          </button>
        </div>

        <div className="p-4 border-b dark:border-gray-700">
          <div className="flex gap-2">
            <div className="flex-1 flex items-center gap-2 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900">
              <Search size={14} className="text-gray-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
                placeholder="Search title or displayId…"
                className="flex-1 bg-transparent text-sm outline-none"
              />
            </div>
            <button
              onClick={search}
              className="px-3 py-1.5 text-sm rounded bg-accent text-white hover:bg-accent-dark"
            >
              Search
            </button>
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-200 text-sm">
            {error}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-2">
          {searching && <div className="p-4 text-sm text-gray-500"><Loader2 size={14} className="inline animate-spin" /> Searching…</div>}
          {!searching && results.length === 0 && (
            <div className="p-4 text-sm text-gray-500 text-center">No matches.</div>
          )}
          <ul className="space-y-1">
            {results.map((r) => {
              const isCross = r.projectId !== parent.projectId;
              return (
                <li key={r._id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <span className={`px-1.5 py-0.5 text-xs rounded ${TYPE_BADGE[r.workItemType] || TYPE_BADGE.task}`}>
                    {r.workItemType}
                  </span>
                  <span className="font-mono text-xs text-gray-500">{r.displayId}</span>
                  <span className="flex-1 text-sm truncate">{r.title}</span>
                  {isCross && (
                    <span
                      className="px-2 py-0.5 text-xs rounded-full border"
                      style={{ borderColor: projectColor(r.projectId), color: projectColor(r.projectId) }}
                    >
                      cross-project
                    </span>
                  )}
                  <button
                    onClick={() => adopt(r)}
                    disabled={adopting === r._id}
                    className="text-xs px-2 py-1 rounded bg-accent text-white hover:bg-accent-dark disabled:opacity-50"
                  >
                    {adopting === r._id ? 'Adopting…' : 'Adopt'}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}

function PortfolioParentsModal({
  projectId,
  onClose,
}: {
  projectId: string;
  onClose: () => void;
}) {
  const projects = useProjectStore((s) => s.projects);
  const [selectedProjectId, setSelectedProjectId] = useState(projectId);
  const [parents, setParents] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [addingProjectId, setAddingProjectId] = useState('');
  const [error, setError] = useState('');

  const load = async (pid: string) => {
    setLoading(true);
    setError('');
    try {
      const data = await portfolioApi.listParents(pid);
      setParents(data.portfolioParentProjectIds || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(selectedProjectId);
  }, [selectedProjectId]);

  const add = async () => {
    if (!addingProjectId) return;
    setError('');
    try {
      const data = await portfolioApi.addParent(selectedProjectId, addingProjectId);
      setParents(data.portfolioParentProjectIds || []);
      setAddingProjectId('');
    } catch (e: any) {
      setError(e.message || 'Add failed');
    }
  };

  const remove = async (pid: string) => {
    setError('');
    try {
      const data = await portfolioApi.removeParent(selectedProjectId, pid);
      setParents(data.portfolioParentProjectIds || []);
    } catch (e: any) {
      setError(e.message || 'Remove failed');
    }
  };

  const projectName = (pid: string) => (projects.find((p: any) => p._id === pid) as any)?.name || pid;
  const candidates = projects.filter((p: any) => p._id !== selectedProjectId && !parents.includes(p._id));

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="font-semibold">Portfolio Governance</h2>
            <p className="text-xs text-gray-500 mt-0.5">Projects allowed to adopt work items from the selected child project</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
            <X size={16} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Child project (items will be adopted FROM)</label>
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900"
            >
              {projects.map((p: any) => (
                <option key={p._id} value={p._id}>{p.name}</option>
              ))}
            </select>
          </div>

          {error && (
            <div className="p-2 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-200 text-sm rounded">{error}</div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium">Authorized adopters</h3>
              {loading && <Loader2 size={12} className="animate-spin text-gray-500" />}
            </div>
            {parents.length === 0 ? (
              <div className="text-sm text-gray-500 italic">None — this project's items cannot be adopted by any other project.</div>
            ) : (
              <ul className="space-y-1">
                {parents.map((pid) => (
                  <li key={pid} className="flex items-center justify-between px-2 py-1.5 bg-gray-50 dark:bg-gray-700/50 rounded">
                    <span className="text-sm">{projectName(pid)}</span>
                    <button
                      onClick={() => remove(pid)}
                      className="text-xs px-2 py-0.5 rounded text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex gap-2 pt-2 border-t dark:border-gray-700">
            <select
              value={addingProjectId}
              onChange={(e) => setAddingProjectId(e.target.value)}
              className="flex-1 px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900"
            >
              <option value="">Select a project to authorize…</option>
              {candidates.map((p: any) => (
                <option key={p._id} value={p._id}>{p.name}</option>
              ))}
            </select>
            <button
              onClick={add}
              disabled={!addingProjectId}
              className="px-3 py-1.5 text-sm rounded bg-accent text-white hover:bg-accent-dark disabled:opacity-50"
            >
              Authorize
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
